import React, { useState, useEffect } from 'react'
import { Button, useToast } from '@chakra-ui/react'
import { FaDownload } from 'react-icons/fa'
import { AnalyticsService } from '../../services/analytics'

interface BeforeInstallPromptEvent extends Event {
  readonly platforms: string[]
  readonly userChoice: Promise<{
    outcome: 'accepted' | 'dismissed'
    platform: string
  }>
  prompt(): Promise<void>
}

export const PWAInstallButton: React.FC = () => {
  const [deferredPrompt, setDeferredPrompt] = useState<BeforeInstallPromptEvent | null>(null)
  const [isInstallable, setIsInstallable] = useState(false)
  const toast = useToast()

  useEffect(() => {
    // Check if the app is already installed
    const isInstalled = window.matchMedia('(display-mode: standalone)').matches || 
                       (window.navigator as any).standalone === true

    if (isInstalled) {
      setIsInstallable(false)
      return
    }

    // Listen for the beforeinstallprompt event
    const handleBeforeInstallPrompt = (e: Event) => {
      e.preventDefault()
      setDeferredPrompt(e as BeforeInstallPromptEvent)
      setIsInstallable(true)
    }

    // Listen for app installed event
    const handleAppInstalled = () => {
      setIsInstallable(false)
      setDeferredPrompt(null)
      toast({
        title: 'App Installed!',
        description: 'AI Scoring App has been successfully installed on your device.',
        status: 'success',
        duration: 5000,
        isClosable: true,
      })
    }

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt)
    window.addEventListener('appinstalled', handleAppInstalled)

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt)
      window.removeEventListener('appinstalled', handleAppInstalled)
    }
  }, [toast])

  const handleInstallClick = async () => {
    if (!deferredPrompt) {
      toast({
        title: 'Installation Not Available',
        description: 'Please use your browser\'s "Add to Home Screen" option.',
        status: 'info',
        duration: 5000,
        isClosable: true,
      })
      return
    }

    try {
      // Log analytics event
      await AnalyticsService.logEvent('pwa_install_prompt_shown')
      
      // Show the install prompt
      await deferredPrompt.prompt()
      
      // Wait for the user to respond to the prompt
      const { outcome } = await deferredPrompt.userChoice
      
      if (outcome === 'accepted') {
        await AnalyticsService.logEvent('pwa_install_accepted')
        toast({
          title: 'Installation Started',
          description: 'Please follow the prompts to install the app.',
          status: 'success',
          duration: 3000,
          isClosable: true,
        })
      } else {
        await AnalyticsService.logEvent('pwa_install_declined')
        toast({
          title: 'Installation Cancelled',
          description: 'You can install the app later using your browser settings.',
          status: 'info',
          duration: 3000,
          isClosable: true,
        })
      }
      
      // Clear the deferred prompt
      setDeferredPrompt(null)
      setIsInstallable(false)
    } catch (error) {
      console.error('Error during installation:', error)
      await AnalyticsService.logError('pwa_install_error', error instanceof Error ? error.message : 'Unknown error')
      toast({
        title: 'Installation Error',
        description: 'There was an error during installation. Please try again.',
        status: 'error',
        duration: 5000,
        isClosable: true,
      })
    }
  }

  // Only show the button if the app is installable
  if (!isInstallable) {
    return null
  }

  return (
    <Button
      leftIcon={<FaDownload />}
      variant="outline"
      size="md"
      onClick={handleInstallClick}
      borderRadius={12}
      fontWeight="normal"
      color="white"
      borderColor="white"
      _hover={{
        transform: 'translateY(-2px)',
        boxShadow: 'lg',
        bg: 'whiteAlpha.100',
      }}
      transition="all 0.2s"
    >
      Download App
    </Button>
  )
}
